import { FC } from "react";

import CustomLink from "@Components/CustomLink";
import Image from "@Components/Image";
import config from "@Config/index";
import getImagePath from "@Utils/getImagePath";
import { pageAsset } from "@Utils/getStaticAssetPath";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

import styles from "./HomeV2Footer.module.scss";

const asset = pageAsset("HomeV3");

/**
 * Where the China app is downloaded. Apple's China storefront, and the Android stores
 * phones in China ship with (Google Play isn't there). A store shows as a link once the
 * app is live on it (`href`); until then its mark sits in the "即将上线" (coming soon)
 * row, unlinked. Marks are each store's own: the AppGallery, GetApps and OPPO 软件商店
 * icons; vivo and HONOR, whose stores publish none, by their brand marks.
 *
 * Up to two live stores show as labelled pills; from three on they become a two-column
 * grid of compact tiles under each store's short name (full name on hover and for screen
 * readers), so the column stays about three rows tall however many go live.
 */
export const CHINA_APP_STORES: {
  id: string;
  name: string;
  /** The tile label once the stores are in a grid. */
  short: string;
  icon: string;
  gaKey: string;
  href?: string;
}[] = [
  {
    id: "Appstore",
    name: "App Store",
    short: "苹果",
    icon: getImagePath(`${config.IMAGE_STATIC_URL}/images/app-store.svg`),
    gaKey: "App Store",
    href: "https://apps.apple.com/cn/app/id6447250749",
  },
  {
    id: "Xiaomi",
    name: "小米应用商店",
    short: "小米",
    icon: asset("store-xiaomi.png"),
    gaKey: "Xiaomi Store",
    href: "https://app.mi.com/details?id=com.amberstudent",
  },
  {
    id: "Huawei",
    name: "华为应用市场",
    short: "华为",
    icon: asset("store-huawei.png"),
    gaKey: "Huawei Store",
  },
  {
    id: "Oppo",
    name: "OPPO 软件商店",
    short: "OPPO",
    icon: asset("store-oppo.png"),
    gaKey: "OPPO Store",
  },
  {
    id: "Vivo",
    name: "vivo 应用商店",
    short: "vivo",
    icon: asset("store-vivo.png"),
    gaKey: "vivo Store",
  },
  {
    id: "Honor",
    name: "荣耀应用市场",
    short: "荣耀",
    icon: asset("store-honor.png"),
    gaKey: "Honor Store",
  },
];
export const liveStores = CHINA_APP_STORES.filter((store) => store.href);
export const soonStores = CHINA_APP_STORES.filter((store) => !store.href);

interface Props {
  /** Fired with the store's analytics name ("App Store", "Xiaomi Store"…). */
  onStoreClick: (gaKey: string) => void;
  /** Centred, for the app modal; the footer column reads from the left. */
  centered?: boolean;
}

/**
 * The China app stores, as the .cn footer's 应用下载 block and the header's app modal
 * both show them: the live stores as pills (or, from three on, a grid of tiles), the rest
 * in a greyed 即将推出 row.
 */
const ChinaAppStores: FC<Props> = ({ onStoreClick, centered = false }) => {
  const { t } = useTranslationHook();
  return (
    <div className={centered ? styles.cnStoresCentered : undefined}>
      <div className={liveStores.length > 2 ? styles.cnStoreGrid : styles.cnStores}>
        {liveStores.map((store) => (
          <CustomLink
            key={store.id}
            href={store.href || ""}
            target="_blank"
            rel="noreferrer"
            isExternal
            className={liveStores.length > 2 ? styles.cnStoreTile : styles.cnStore}
            dataTestId={`v2-footer-get-app-${store.id}`}
            onClick={() => onStoreClick(store.gaKey)}
            title={store.name}
            aria-label={store.name}
          >
            <Image src={store.icon} alt="" width="20" height="20" />
            {liveStores.length > 2 ? store.short : store.name}
          </CustomLink>
        ))}
      </div>
      {soonStores.length > 0 && (
        <div className={styles.cnSoon}>
          <span className={styles.cnSoonLabel}>{t("coming soon")}</span>
          <ul className={styles.cnSoonList}>
            {soonStores.map((store) => (
              <li key={store.id} title={`${store.name} · ${t("coming soon")}`}>
                <Image src={store.icon} alt={store.name} width="24" height="24" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(ChinaAppStores, {
  componentName: "ChinaAppStores-HomeV3",
  showForChina: true,
  showForWhiteLable: false,
});
